/**
 * Direct USB printing for Zebra label printers via WebUSB.
 *
 * Why this exists: the Chrome → Windows driver → Zebra path was producing
 * duplicate/rotated labels even though our generated PDF is a perfectly
 * clean single page. Going around Windows entirely and sending raw EPL
 * commands over USB removes every intermediary that could be misbehaving.
 *
 * Requirements:
 *   - HTTPS origin (Chrome only allows WebUSB on secure origins)
 *   - Chrome / Edge (Safari + Firefox don't support WebUSB yet)
 *   - User clicks "Pair Printer" once → browser shows device picker
 *   - Pairing persists across sessions (navigator.usb.getDevices)
 *
 * Printer command language: EPL (the GC420d's current default).
 * If the user has a ZPL-only printer we can flip the buildStickerEPL function
 * to emit ZPL — same shape.
 */

import { toast } from 'sonner';

const ZEBRA_VID = 0x0a5f; // Zebra Technologies USB vendor id

export function webUsbSupported() {
  return typeof navigator !== 'undefined' && 'usb' in navigator;
}

/**
 * One-time pairing — browser shows a chooser of USB devices.
 * Tries the Zebra-VID filter first; if that returns no devices the user
 * can hit pair again with `showAll=true` to see all USB devices and
 * locate the printer manually (some Zebra OEMs use non-Zebra VIDs, and
 * Windows-claimed devices sometimes only surface under the unfiltered list).
 */
export async function pairZebraPrinter(showAll = false) {
  if (!webUsbSupported()) {
    throw new Error('WebUSB is not supported in this browser. Use Chrome or Edge on a secure (https) page.');
  }
  const filters = showAll
    ? [{}] // no filter — show every USB device the OS exposes
    : [{ vendorId: ZEBRA_VID }];
  return await navigator.usb.requestDevice({ filters });
}

/**
 * Returns the first already-paired Zebra device, or null.
 */
export async function getPairedZebra() {
  if (!webUsbSupported()) return null;
  const devices = await navigator.usb.getDevices();
  return devices.find((d) => d.vendorId === ZEBRA_VID) || null;
}

/**
 * Send a buffer (or string) of EPL/ZPL commands to the paired Zebra.
 * Opens + claims the printer interface, writes, then releases.
 */
export async function sendToZebra(commands) {
  const device = await getPairedZebra();
  if (!device) {
    throw new Error('No Zebra printer paired yet. Click "Pair Zebra Printer" in Settings first.');
  }

  if (!device.opened) await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);

  // Walk the configuration to find the bulk OUT endpoint on the printer interface.
  let iface = null;
  let outEndpoint = null;
  for (const i of device.configuration.interfaces) {
    for (const alt of i.alternates) {
      // USB printer class is 7
      if (alt.interfaceClass === 7 || alt.interfaceClass === 0xff) {
        const ep = alt.endpoints.find((e) => e.direction === 'out');
        if (ep) {
          iface = i;
          outEndpoint = ep;
          break;
        }
      }
    }
    if (iface) break;
  }
  // Fallback — just pick the first interface with any OUT endpoint.
  if (!iface) {
    for (const i of device.configuration.interfaces) {
      const alt = i.alternates[0];
      const ep = alt.endpoints.find((e) => e.direction === 'out');
      if (ep) {
        iface = i;
        outEndpoint = ep;
        break;
      }
    }
  }
  if (!iface || !outEndpoint) {
    throw new Error('Could not locate a USB OUT endpoint on the printer.');
  }

  try {
    await device.claimInterface(iface.interfaceNumber);
  } catch (e) {
    // Windows may be holding the interface via its driver. Give the user a clear hint.
    throw new Error(
      'Could not claim the printer USB interface. Windows may have it locked — try disconnecting the Zebra driver in Device Manager, or use Zadig to install WinUSB.'
    );
  }

  const bytes =
    typeof commands === 'string' ? new TextEncoder().encode(commands) : commands;
  await device.transferOut(outEndpoint.endpointNumber, bytes);

  try {
    await device.releaseInterface(iface.interfaceNumber);
  } catch (_) {
    /* ignore release errors */
  }
}

/**
 * Build raw EPL commands for a single job sticker.
 * Coordinates are in dots; the GC420d is 203 DPI = 8 dots/mm.
 *
 * EPL "A" command:
 *   A <x>,<y>,<rotation>,<font>,<hMult>,<vMult>,<reverse>,"<text>"
 *   font 3 base = 12×20 dots, scaled ×2 → 24×40 dots (≈ 5mm tall — readable)
 */
export function buildStickerEPL(job, settings) {
  const labelWmm = settings.sticker_width_mm || 40;
  const labelHmm = settings.sticker_height_mm || 28;
  const dotsPerMm = 8; // 203 DPI ≈ 8 dots/mm
  const widthDots = Math.round(labelWmm * dotsPerMm);
  const heightDots = Math.round(labelHmm * dotsPerMm);

  // EPL escape — strip double quotes from text so we don't break the A command.
  const esc = (v) => String(v ?? '').replace(/"/g, "'").trim();
  const jobLine = `Job ${esc(job.job_number)}`;
  const nameLine = esc(job.customer_name);
  const phoneLine = esc(job.mobile || '');

  // Use font 3 scaled ×2 — gives a 24-dot-wide × 40-dot-tall monospace cell.
  const font = 3;
  const mult = 2;
  const charW = 12 * mult; // 24 dots
  const lineH = 20 * mult; // 40 dots

  // Vertical centering of the 3 lines within the label. A one-line gap
  // is inserted between the job number and the customer name so the
  // Job # visually stands apart from the two customer lines beneath it.
  const lineGap = 8; // dots between the 2 customer lines
  const jobGap = lineH; // one full line-height gap after the Job # line
  const totalTextH = lineH * 3 + jobGap + lineGap;
  const topY = Math.max(0, Math.floor((heightDots - totalTextH) / 2));

  const lineY = (i) => {
    if (i === 0) return topY;                             // Job #
    if (i === 1) return topY + lineH + jobGap;            // Customer name (after 1-line gap)
    return topY + 2 * lineH + jobGap + lineGap;           // Mobile
  };

  // Horizontal centering — fonts are monospaced so width = charW * charCount.
  const centerX = (text) => {
    const w = text.length * charW;
    return Math.max(0, Math.floor((widthDots - w) / 2));
  };

  // q = label width in dots, Q = label length + gap (24 dots ≈ 3mm gap is typical for diecut)
  const cmds = [
    'N', // clear image buffer
    `q${widthDots}`,
    `Q${heightDots},24`,
    `A${centerX(jobLine)},${lineY(0)},0,${font},${mult},${mult},N,"${jobLine}"`,
    `A${centerX(nameLine)},${lineY(1)},0,${font},${mult},${mult},N,"${nameLine}"`,
    `A${centerX(phoneLine)},${lineY(2)},0,${font},${mult},${mult},N,"${phoneLine}"`,
    'P1', // print 1 label
    '',
  ];
  return cmds.join('\n');
}

/**
 * Convenience — pairs (if not already) and prints the sticker via USB.
 * Returns true on success, false on any failure (caller decides on fallback).
 */
export async function printStickerViaUsb(job, settings) {
  try {
    const epl = buildStickerEPL(job, settings);
    await sendToZebra(epl);
    toast.success('Sticker sent to Zebra via USB');
    return true;
  } catch (e) {
    toast.error(e.message || 'USB print failed');
    return false;
  }
}
